import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api/client.js";
import type { ChangeSet } from "@archlang/engine";
import type { Module, DependencyEdge } from "./types.js";
import { deserializeChangeSet, type WireChangeSet } from "./deserializeChangeSet.js";

export interface UseGitDiffArgs {
  readonly slug: string;
  readonly repo: string;
  readonly from: string;
  readonly to: string;
}

/** A resolution diagnostic on a package's proposed (after) state. */
export interface DiffDiagnostic {
  readonly severity: "error" | "warning" | "info" | "hint";
  readonly message: string;
  readonly code?: string;
  readonly source?: { file?: string; start?: { line: number; column: number } };
}

/** One package's diff within a (possibly multi-package) monorepo changeset. */
export interface PackageDiff {
  /** Bare package-root path. "" = repo root. */
  readonly key: string;
  /** Dotted package name; undefined for the implicit repo-root package. */
  readonly name?: string;
  readonly implicit?: boolean;
  readonly before: { model: { modules: readonly Module[] }; edges: readonly DependencyEdge[] };
  readonly after:  { model: { modules: readonly Module[] }; edges: readonly DependencyEdge[] };
  readonly changeSet: ChangeSet;
  /** Errors/warnings resolving the proposed state ("does this version compile?"). */
  readonly diagnostics?: readonly DiffDiagnostic[];
}

export interface DiffPayload {
  readonly from: { ref: string; oid: string };
  readonly to:   { ref: string; oid: string };
  /** Only packages that changed, grouped. Empty when nothing changed. */
  readonly packages: readonly PackageDiff[];
}

/** Wire form: same as PackageDiff but changeSet is the un-deserialized wire type. */
type WirePackageDiff = Omit<PackageDiff, "changeSet"> & { changeSet: WireChangeSet };
type WireDiffPayload = Omit<DiffPayload, "packages"> & { packages: readonly WirePackageDiff[] };

/** Deserialize every package's changeSet from the wire payload. */
export function deserializeDiffPayload(raw: WireDiffPayload): DiffPayload {
  return {
    from: raw.from,
    to: raw.to,
    packages: raw.packages.map((p) => ({
      ...p,
      changeSet: deserializeChangeSet(p.changeSet),
    })),
  };
}

export function useGitDiff(args: UseGitDiffArgs) {
  return useQuery({
    queryKey: ["diff", args.slug, args.repo, args.from, args.to],
    queryFn: async ({ signal }): Promise<DiffPayload> => {
      const url = `/api/ws/${encodeURIComponent(args.slug)}/r/${encodeURIComponent(args.repo)}/diff?from=${encodeURIComponent(args.from)}&to=${encodeURIComponent(args.to)}`;
      // Via apiFetch (not raw fetch) so a 401 triggers the sign-in redirect
      // instead of surfacing a bare "diff failed: 401" (bugloop L2 #4).
      const raw = await apiFetch<WireDiffPayload>(url, { signal });
      return deserializeDiffPayload(raw);
    },
    enabled: Boolean(args.from && args.to),
    staleTime: 5 * 60 * 1000,
  });
}
